import React, { useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { get } from 'lodash';
import classNames from 'classnames';
import Box from 'core/components/Box';
import Checkbox from 'core/components/Checkbox';
import Flex from 'core/components/Flex';
import Text from 'core/components/Text';
import Icon from 'core/components/Icon';
import styled from 'styled-components';
import Tooltip from 'core/components/Tooltip';
import CELL_ACTIONS from './CELL_ACTIONS';
import CELL_TYPES from './CELL_TYPES';
import { getCellStyle, isResizableColumn } from './Cell';

// Refresh body cell NodeList every N frames while dragging to avoid querying each frame
const CELL_QUERY_INTERVAL = 15;

function getAllRightResizableColumnIndices(columns, currentIndex) {
  const indices = [];
  for (let i = currentIndex + 1; i < columns.length; i += 1) {
    if (isResizableColumn(columns[i])) {
      indices.push(i);
    }
  }
  return indices;
}

const StyledSortIcon = styled(Icon)`
  transition: transform 0.2s ease-out;
  transform: ${(props) => (props.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const ResizeHandle = styled.div`
  width: 10px;
  cursor: ${({ $isResizable }) => ($isResizable ? 'col-resize' : 'default')};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  z-index: 1;
  border-right: ${({ $isResizable, theme }) => ($isResizable ? `1px solid ${theme.borderColors.thin}` : 'none')};
`;

const SelectAllCheckbox = (props) => {
  const { collection } = props;

  return (
    <Checkbox
      aria-label="Select all"
      indeterminate={
        !!(
          collection.selected &&
          collection.selected.length > 0 &&
          collection.selected.length < collection.models.length
        )
      }
      key="select"
      checked={!!collection.isAllSelected}
      onChange={() => null}
      onClick={(e) => {
        e.stopPropagation();
        collection.selectAll();
      }}
    />
  );
};

const HeaderCell = (props) => {
  const {
    $setup,
    collection,
    column,
    columns,
    index,
    useSortTooltips,
    noHighlightSorted,
    onSort,
    resizedColumns,
    tabPrefId,
    persistColumnWidths = true,
    columnWidthsRef,
    setIsResizing,
    updateGrid
  } = props;
  const headerCellRef = useRef(null);
  const tableContainerRef = useRef(null);

  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const resizingRef = useRef(false);
  const rafPendingRef = useRef(false);
  const lastAppliedWidthRef = useRef(column.width || columnWidthsRef?.[column.name || column.id || column.key] || null);
  const ignoreClickAfterResizeRef = useRef(false);
  const columnCellsRef = useRef([]);
  const frameCounterRef = useRef(0);

  const rightColumnsRef = useRef([]); // [{ index, startWidth, headerCell, bodyCells }, ...]
  const lastAppliedRightWidthsRef = useRef([]);

  const { field, direction } = collection.sortState;
  const sortable = column.sortable !== false && column.type !== CELL_TYPES.ACTION;
  const hasOnHeaderClick = typeof column.onHeaderClick === 'function';
  const interactive = sortable || hasOnHeaderClick;
  const serverSortable = column.serverSortable === true && column.type !== CELL_TYPES.ACTION;
  const sorted = field && (field === column.name || field === column.sortField);
  const columnName = column.sortField || column.name;
  const selectAllCheckbox = (column.actions && column.actions.includes(CELL_ACTIONS.SELECT)) || column.customSelect;
  const hasRightResizableColumns = getAllRightResizableColumnIndices(columns, index).length > 0;
  const isResizable =
    column.resizable !== false &&
    column.type !== CELL_TYPES.ACTION &&
    column.key !== 'select' &&
    hasRightResizableColumns;

  const onClick = () => {
    // Skip sort immediately after resize to avoid accidental sorts
    if (ignoreClickAfterResizeRef.current) {
      ignoreClickAfterResizeRef.current = false;
      return;
    }
    if (sortable) {
      collection[serverSortable ? 'serverSort' : 'sort'](columnName);

      // serverSort provides serverSortFn for callbacks; for sync sorting we use onSort
      if (onSort) {
        onSort({ field: collection.sortState.field, direction: collection.sortState.direction });
      }
    }
    if (hasOnHeaderClick) {
      column.onHeaderClick(column);
    }
  };

  const calculateSpannedWidth = (startIndex, colSpan) => {
    let totalWidth = 0;
    for (let j = startIndex; j < startIndex + colSpan && j < columns.length; j += 1) {
      const spannedCol = columns[j];
      const spannedKey = spannedCol.name || spannedCol.id || spannedCol.key;
      const resizedWidth =
        (columnWidthsRef && columnWidthsRef[spannedKey]) || (resizedColumns && resizedColumns[spannedKey]);

      if (resizedWidth) {
        totalWidth += resizedWidth;
      } else if (spannedCol.width) {
        totalWidth += spannedCol.width;
      } else {
        totalWidth += spannedCol.flexBasis || 100;
      }
    }
    return totalWidth;
  };

  const updateSpanningCells = () => {
    if (!columns || !resizedColumns) {
      return;
    }

    for (let i = 0; i <= index; i += 1) {
      const col = columns[i];

      const totalRowColSpan = typeof col.colSpan === 'function' ? col.colSpan(null, true) : col.colSpan || 1;

      const overlayColSpan =
        typeof col.colSpan === 'function' ? col.colSpan({ isOverlay: true, get: () => true }, false) : col.colSpan || 1;

      const spanningColKey = col.name || col.id || col.key || col.type;

      const totalRowSpansResized = totalRowColSpan > 1 && i + totalRowColSpan > index;
      const overlaySpansResized = overlayColSpan > 1 && i + overlayColSpan > index;

      if (!totalRowSpansResized && !overlaySpansResized) {
        /* eslint-disable no-continue */
        continue;
      }

      // calculate widths for each case (only if they span the resized column)
      const totalRowWidth = totalRowSpansResized ? calculateSpannedWidth(i, totalRowColSpan) : null;
      const overlayWidth = overlaySpansResized ? calculateSpannedWidth(i, overlayColSpan) : null;

      const container = tableContainerRef.current || document;
      const spanningCells = container.querySelectorAll(`[data-col-key="${CSS.escape(spanningColKey)}"]`);

      spanningCells.forEach((cellEl) => {
        const row = cellEl.closest('.tr');
        if (!row) {
          return;
        }

        const isTotalRow = row.classList.contains('total');

        let widthToApply = null;

        if (isTotalRow && totalRowWidth !== null) {
          widthToApply = totalRowWidth;
        } else if (!isTotalRow) {
          // check if this is an overlay row by looking for hidden cells
          const isOverlayRow =
            row.querySelector('[style*="display: none"]') || row.querySelector('[style*="display:none"]');
          if (isOverlayRow && overlayWidth !== null) {
            widthToApply = overlayWidth;
          }
        }

        if (widthToApply !== null) {
          cellEl.style.width = `${widthToApply}px`;
          cellEl.style.minWidth = `${widthToApply}px`;
          cellEl.style.maxWidth = `${widthToApply}px`;
          cellEl.style.flexBasis = `${widthToApply}px`;
          cellEl.style.flexGrow = '0';
          cellEl.style.flexShrink = '0';
        }
      });
    }
  };

  const saveResizedColumns = () => {
    if (tabPrefId && persistColumnWidths) {
      const tabPrefs = get($setup, 'settings.tabPrefs') || {};
      if (!tabPrefs.resizedColumns) {
        tabPrefs.resizedColumns = {};
      }
      if (!tabPrefs.resizedColumns[tabPrefId]) {
        tabPrefs.resizedColumns[tabPrefId] = {};
      }
      tabPrefs.resizedColumns[tabPrefId] = {
        ...tabPrefs.resizedColumns[tabPrefId],
        ...resizedColumns
      };
      $setup.updateSettings({ tabPrefs });
    }
  };
  const applyWidth = (w, rightColumnWidths = []) => {
    const el = headerCellRef.current;
    if (!el) {
      return;
    }

    const rightWidthsChanged = rightColumnWidths.some((width, i) => lastAppliedRightWidthsRef.current[i] !== width);
    if (lastAppliedWidthRef.current === w && !rightWidthsChanged) {
      return;
    }

    const hasRightColumns = rightColumnsRef.current.length > 0;

    // for the last resizable column (no right columns), use flexGrow: 1 to fill remaining space
    if (!hasRightColumns) {
      el.style.width = '';
      el.style.minWidth = `${w}px`;
      el.style.maxWidth = '';
      el.style.flexBasis = `${w}px`;
      el.style.flexGrow = '1';
      el.style.flexShrink = '0';
    } else {
      // apply fixed width to the column being resized
      el.style.width = `${w}px`;
      el.style.minWidth = `${w}px`;
      el.style.maxWidth = `${w}px`;
      el.style.flexBasis = `${w}px`;
      el.style.flexGrow = '0';
      el.style.flexShrink = '0';
    }
    lastAppliedWidthRef.current = w;

    // apply to body cells for this column
    if (columnCellsRef.current && columnCellsRef.current.length) {
      for (let i = 0; i < columnCellsRef.current.length; i += 1) {
        const cellEl = columnCellsRef.current[i];
        if (cellEl) {
          if (!hasRightColumns) {
            cellEl.style.width = '';
            cellEl.style.minWidth = `${w}px`;
            cellEl.style.maxWidth = '';
            cellEl.style.flexBasis = `${w}px`;
            cellEl.style.flexGrow = '1';
            cellEl.style.flexShrink = '0';
          } else {
            cellEl.style.width = `${w}px`;
            cellEl.style.minWidth = `${w}px`;
            cellEl.style.maxWidth = `${w}px`;
            cellEl.style.flexBasis = `${w}px`;
            cellEl.style.flexGrow = '0';
            cellEl.style.flexShrink = '0';
          }
        }
      }
    }

    rightColumnsRef.current.forEach((rightColInfo, i) => {
      const newWidth = rightColumnWidths[i];
      if (newWidth === undefined) {
        return;
      }

      const { headerCell, bodyCells } = rightColInfo;

      if (headerCell) {
        headerCell.style.width = `${newWidth}px`;
        headerCell.style.minWidth = `${newWidth}px`;
        headerCell.style.maxWidth = `${newWidth}px`;
        headerCell.style.flexBasis = `${newWidth}px`;
        headerCell.style.flexGrow = '0';
        headerCell.style.flexShrink = '0';
      }

      if (bodyCells && bodyCells.length) {
        for (let j = 0; j < bodyCells.length; j += 1) {
          const cellEl = bodyCells[j];
          if (cellEl) {
            cellEl.style.width = `${newWidth}px`;
            cellEl.style.minWidth = `${newWidth}px`;
            cellEl.style.maxWidth = `${newWidth}px`;
            cellEl.style.flexBasis = `${newWidth}px`;
            cellEl.style.flexGrow = '0';
            cellEl.style.flexShrink = '0';
          }
        }
      }
    });

    lastAppliedRightWidthsRef.current = [...rightColumnWidths];

    updateSpanningCells();
  };

  const pointerMoveHandler = (e) => {
    if (!resizingRef.current) {
      return;
    }
    const diff = e.clientX - startXRef.current;
    const minWidth = 50;
    const columnMaxWidth = column.maxWidth || Infinity;

    const rightCols = rightColumnsRef.current;
    const hasRightColumns = rightCols.length > 0;

    let maxDiff = columnMaxWidth - startWidthRef.current;
    let minDiff = minWidth - startWidthRef.current;

    if (hasRightColumns) {
      const totalRightStartWidth = rightCols.reduce((sum, col) => sum + col.startWidth, 0);

      // when we grow (positive diff), right columns shrink proportionally
      // they collectively can't go below their sum of minWidths
      const totalMinWidth = rightCols.length * minWidth;
      const maxGrowth = totalRightStartWidth - totalMinWidth;

      // when we shrink (negative diff), right columns grow proportionally
      // each right column can't exceed its maxWidth
      let maxShrink = 0;
      rightCols.forEach((colInfo) => {
        const col = columns[colInfo.index];
        const colMaxWidth = col?.maxWidth || Infinity;
        if (colMaxWidth !== Infinity) {
          // proportional share of shrink based on this column's width ratio
          const ratio = colInfo.startWidth / totalRightStartWidth;
          maxShrink += (colMaxWidth - colInfo.startWidth) / ratio;
        } else {
          maxShrink = Infinity;
        }
      });

      maxDiff = Math.min(maxDiff, maxGrowth);
      if (maxShrink !== Infinity) {
        minDiff = Math.max(minDiff, -maxShrink);
      }
    }

    const constrainedDiff = Math.min(Math.max(diff, minDiff), maxDiff);
    const newWidth = startWidthRef.current + constrainedDiff;

    const newRightWidths = [];
    if (hasRightColumns) {
      const totalRightStartWidth = rightCols.reduce((sum, col) => sum + col.startWidth, 0);

      rightCols.forEach((colInfo) => {
        const ratio = colInfo.startWidth / totalRightStartWidth;
        let newColWidth = colInfo.startWidth - constrainedDiff * ratio;
        newColWidth = Math.max(newColWidth, minWidth);
        newRightWidths.push(newColWidth);
      });
    }

    const columnKey = column.name || column.id || column.key;
    if (columnKey && resizedColumns) {
      resizedColumns[columnKey] = newWidth;
      if (columnWidthsRef) {
        columnWidthsRef[columnKey] = newWidth;
      }
    }
    column.width = newWidth;

    rightCols.forEach((colInfo, i) => {
      const col = columns[colInfo.index];
      const colKey = col.name || col.id || col.key;
      const newColWidth = newRightWidths[i];

      if (colKey && resizedColumns) {
        resizedColumns[colKey] = newColWidth;
        if (columnWidthsRef) {
          columnWidthsRef[colKey] = newColWidth;
        }
      }
      col.width = newColWidth;
    });

    if (!rafPendingRef.current) {
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        // Periodically refresh list of visible cells (virtualization may reuse nodes)
        frameCounterRef.current += 1;
        if (frameCounterRef.current % CELL_QUERY_INTERVAL === 0 || columnCellsRef.current.length === 0) {
          const key = column.name || column.id || column.key || column.type;
          const container = tableContainerRef.current || document;
          columnCellsRef.current = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(key)}"]`));

          rightCols.forEach((colInfo) => {
            const col = columns[colInfo.index];
            const colKey = col.name || col.id || col.key || col.type;
            colInfo.bodyCells = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(colKey)}"]`));
          });
        }
        applyWidth(newWidth, newRightWidths);
      });
    }
  };

  const pointerUpHandler = (e) => {
    if (!resizingRef.current) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    document.removeEventListener('pointermove', pointerMoveHandler, true);
    document.removeEventListener('pointerup', pointerUpHandler, true);
    document.body.style.userSelect = '';
    resizingRef.current = false;
    if (setIsResizing) {
      setIsResizing(false);
    }
    ignoreClickAfterResizeRef.current = true;
    saveResizedColumns();
    if (updateGrid) {
      updateGrid();
    }
  };

  const handlePointerDown = (e) => {
    if (!isResizable) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    if (!tableContainerRef.current) {
      tableContainerRef.current = headerCellRef.current?.closest('[role="table"]');
    }

    const rect = headerCellRef.current.getBoundingClientRect();
    startXRef.current = e.clientX;
    startWidthRef.current = rect.width;
    resizingRef.current = true;
    if (setIsResizing) {
      setIsResizing(true);
    }
    document.body.style.userSelect = 'none';

    const rightColIndices = getAllRightResizableColumnIndices(columns, index);
    rightColumnsRef.current = rightColIndices.map((idx) => ({
      index: idx,
      startWidth: 0, // will be set below when we scan header cells
      headerCell: null,
      bodyCells: []
    }));

    // capture all current column widths from DOM before resize starts AND lock them
    // all columns get locked to fixed widths during resize
    if (columns && columnWidthsRef) {
      const headerRow = headerCellRef.current?.closest('.tr');
      if (headerRow) {
        const headerCells = headerRow.querySelectorAll('.th');
        headerCells.forEach((cell, idx) => {
          if (idx < columns.length) {
            const col = columns[idx];
            const colKey = col.name || col.id || col.key;
            if (colKey) {
              const cellRect = cell.getBoundingClientRect();
              const { width } = cellRect;
              columnWidthsRef[colKey] = width;
              if (resizedColumns) {
                resizedColumns[colKey] = width;
              }

              const rightColInfo = rightColumnsRef.current.find((rc) => rc.index === idx);
              if (rightColInfo) {
                rightColInfo.startWidth = width;
                rightColInfo.headerCell = cell;
              }

              // lock all columns to fixed width
              cell.style.width = `${width}px`;
              cell.style.minWidth = `${width}px`;
              cell.style.maxWidth = `${width}px`;
              cell.style.flexBasis = `${width}px`;
              cell.style.flexGrow = '0';
              cell.style.flexShrink = '0';

              // lock body cells for this column (skip spanning cells - they'll be fixed below)
              const container = tableContainerRef.current || document;
              const bodyCells = container.querySelectorAll(`[data-col-key="${CSS.escape(colKey)}"]`);
              bodyCells.forEach((bodyCell) => {
                // skip cells in rows that have spanning behavior (TotalRow or overlay rows)
                const row = bodyCell.closest('.tr');
                if (row) {
                  const isTotalRow = row.classList.contains('total');
                  const isOverlayRow =
                    row.querySelector('[style*="display: none"]') || row.querySelector('[style*="display:none"]');
                  if (isTotalRow || isOverlayRow) {
                    return; // skip - these will be handled by updateSpanningCells
                  }
                }
                bodyCell.style.width = `${width}px`;
                bodyCell.style.minWidth = `${width}px`;
                bodyCell.style.maxWidth = `${width}px`;
                bodyCell.style.flexBasis = `${width}px`;
                bodyCell.style.flexGrow = '0';
                bodyCell.style.flexShrink = '0';
              });
            }
          }
        });
      }
    }

    updateSpanningCells();

    const key = column.name || column.id || column.key || column.type;
    const container = tableContainerRef.current || document;
    columnCellsRef.current = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(key)}"]`));

    rightColumnsRef.current.forEach((rightColInfo) => {
      const col = columns[rightColInfo.index];
      const colKey = col.name || col.id || col.key || col.type;
      rightColInfo.bodyCells = Array.from(container.querySelectorAll(`[data-col-key="${CSS.escape(colKey)}"]`));
    });

    document.addEventListener('pointermove', pointerMoveHandler, true);
    document.addEventListener('pointerup', pointerUpHandler, true);
  };

  const handleHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick();
    }
  };

  const className = classNames('th', { sortable, sorted, interactive, noHighlightSorted });

  // Base style (flex sizing) unless a width already applied directly
  const baseStyle = getCellStyle({ ...props, resizedColumns });
  const style = baseStyle; // direct DOM width overrides actual flex calc when present

  const align = column.headerAlign || column.align;
  const padding = column.headerPadding ?? column.padding ?? '6px 0 6px 12px';
  const justifyContent = column.flexDirection !== 'column' ? column.justifyContent : undefined;
  const dirString = sorted && direction === 'asc' ? 'Ascending' : 'Descending';
  const ariaProps = {};
  if (dirString) {
    ariaProps['aria-sort'] = dirString;
  }
  let labelGuess = column.label || column.name;
  if (labelGuess?.length === 0 && column.type === 'action') {
    labelGuess = 'action options';
  }
  ariaProps['aria-label'] = labelGuess;
  return (
    <Flex
      {...ariaProps}
      ref={headerCellRef}
      className={className}
      onClick={onClick}
      css={{ cursor: interactive ? 'pointer' : 'default', position: 'relative' }}
      style={style}
      alignItems="flex-end"
      justifyContent={justifyContent || (align === 'right' ? 'flex-end' : align)}
      p={padding}
      role="columnheader"
      data-col-key={column.name || column.id || column.key || column.type}
      onKeyDown={handleHeaderKeyDown}
      overflow="hidden"
    >
      {selectAllCheckbox && <SelectAllCheckbox collection={collection} tabIndex={interactive ? 0 : -1} />}
      {!sorted && <Text tabIndex={interactive ? 0 : -1}>{column.label || ''}</Text>}
      {sorted && (column.label || '')}
      {sorted && !useSortTooltips && (
        <StyledSortIcon icon="chevron-up" direction={direction} tabIndex={interactive ? 0 : -1} />
      )}
      {sorted && useSortTooltips && (
        <Tooltip content={<Text>{dirString}</Text>}>
          <StyledSortIcon icon="chevron-up" direction={direction} tabIndex={interactive ? 0 : -1} />
        </Tooltip>
      )}
      {!sorted && !selectAllCheckbox && sortable && align !== 'right' && <Box width={16} />}
      {tabPrefId && resizedColumns && (
        <ResizeHandle $isResizable={isResizable} onPointerDown={isResizable ? handlePointerDown : undefined} />
      )}
    </Flex>
  );
};

export default inject('$setup')(observer(HeaderCell));
